import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import UnscheduledLines from "./UnscheduledLine";
import {
    GridLines,
    GridGroups,
    CurrentShiftDashedLine,
} from "@/modules/pg/shared/planningTable/ganttChart/GanttChartGrid";
import Loader from "@/modules/shared/ui/Loader";
import SelectableSvg, { type Selection } from "@/modules/pg/shared/SelectableSvg";
import { classNames } from "@/lib/util";
import { PropertiesLogic } from "@/modules/pg/shared/planningTable/reducers/logic/businessLogic";
import { zoomGanttChartReset } from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import {
    applyGanttZoomFromSelection,
    GANTT_ORDER_RECT_TARGET_SELECTOR,
} from "@/modules/pg/shared/planningTable/ganttChart/ganttRectangleSelection";

import type {
    UpdatePropertiesActionType,
    GanttChartSourceTypes,
    UpdatePlanningTableWidthAction,
} from "@/modules/pg/shared/planningTable/reducers/gantt/properties";
import type { ReportState } from "@/modules/pg/shared/planningTable/reducers/report";
import type { ReduxState } from "@/modules/pg/shared/planningTable/reducers/reducers";
import type { ResetGanttAction } from "@/modules/pg/shared/planningTable/reducers/common";
import type { SetIsCtrlDown } from "@/modules/pg/shared/planningTable/reducers/planningTable";
import * as t2 from "@/lib/backend/reports.generated.types";

const getReportCreatedAt = (report: ReportState) => {
    if (!report || !report.report_data) return null;
    return report.report_data.created_at;
};

type ActionTypes = UpdatePropertiesActionType | ResetGanttAction | UpdatePlanningTableWidthAction | SetIsCtrlDown;

type OwnProps = {
    line_group_uuid: string;
    show_planning_details_filter: boolean;
    source_type: GanttChartSourceTypes;
    show_only_unscheduled?: boolean;
};

type Props = OwnProps & ReduxProps;

type State = {
    properties_loaded: boolean;
};

class UnscheduledGanttChart extends React.Component<Props, State> {
    phantomNode: React.RefObject<SVGTextElement> | null = null;

    state: State = {
        properties_loaded: false,
    };

    getIsPropertiesLoaded = () => {
        return this.props.properties.loaded;
    };

    getSvgAttributes() {
        let svg_attributes = {};
        const width = this.props.properties.width;
        const height = PropertiesLogic.getHeight(this.props.properties, this.props.source_type);

        if (width || height) {
            svg_attributes = {
                width: width,
                height: height,
            };
        }

        return svg_attributes;
    }

    getSourceType = () => {
        return this.props.source_type || this.props.properties.source_type;
    };

    handleUnscheduledZoomRectangle = (selection: Selection) => {
        applyGanttZoomFromSelection(selection, {
            source_type: this.getSourceType(),
            dispatch: this.props.reduxDispatch,
        });
    };

    handleUnscheduledZoomDoubleClick = () => {
        zoomGanttChartReset(this.getSourceType(), this.props.reduxDispatch);
    };

    getLoader = () => {
        if (getReportCreatedAt(this.props.report)) {
            const properties = this.props.properties;
            if (properties) {
                const is_gantt_loading = this.props.report_loading;

                if (is_gantt_loading) {
                    const pane = document.querySelector("#pane2");
                    let rect: DOMRect | null = null;
                    if (pane) {
                        rect = pane.getBoundingClientRect();
                        const style: React.CSSProperties = {
                            backgroundColor: "white",
                            opacity: "0.3",
                            position: "absolute",
                            width: rect.width,
                            height: rect.height,
                        };
                        return <div style={style}></div>;
                    }
                }
                return null;
            }
        }

        return (
            <div>
                {" "}
                <Loader small />{" "}
            </div>
        );
    };

    render() {
        const svg_attributes = this.getSvgAttributes();
        const class_name = `${this.props.properties.no_overflow ? "" : "chart-wrapper"}`;
        const shifts = this.props.shifts ? this.props.shifts[0].shifts : [];
        const zoomInteractionEnabled = !this.props.is_ctrl_down;

        return (
            <div id={this.props.source_type} style={{ zIndex: 1, opacity: 1 }} className={class_name}>
                <svg id="gantt-chart-unscheduled" {...svg_attributes}>
                    <g onDoubleClick={zoomInteractionEnabled ? this.handleUnscheduledZoomDoubleClick : undefined}>
                        <SelectableSvg
                            className={classNames({
                                "selectable-svg--zooming": zoomInteractionEnabled,
                            })}
                            onSelect={this.handleUnscheduledZoomRectangle}
                            target_selector={GANTT_ORDER_RECT_TARGET_SELECTOR}
                        >
                            <g id="grid">
                                <text
                                    ref={this.phantomNode}
                                    fontSize={"14px"}
                                    fontFamily={"'Roboto', sans-serif"}
                                    fontWeight={"400"}
                                />
                                <GridLines
                                    gantt_chart_properties={this.props.properties}
                                    source_type={this.props.source_type}
                                    shifts={shifts}
                                    shift_x_positions={this.props.shift_x_positions}
                                />
                                <GridGroups
                                    gantt_chart_properties={this.props.properties}
                                    source_type={this.props.source_type}
                                    shifts={shifts}
                                    shift_x_positions={this.props.shift_x_positions}
                                />
                                <UnscheduledLines source_type={this.props.source_type} />
                                <CurrentShiftDashedLine
                                    gantt_chart_properties={this.props.properties}
                                    source_type={this.props.source_type}
                                    shifts={shifts}
                                    shift_x_positions={this.props.shift_x_positions}
                                />
                            </g>
                        </SelectableSvg>
                    </g>
                </svg>
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const loading: boolean = state.gantt_chart_lines_orders.loading;
    const properties = state.gantt_chart_properties;
    const planning_table = state.gantt_chart_planning_table;
    const report = state.gantt_chart_report.report_data;
    const is_ctrl_down = state.gantt_chart_planning_table.is_ctrl_down;

    let shifts: t2.ISimulationReportOrderLineShiftFeature[] = [];
    if (report) {
        shifts = report.result.line_shift_features;
    }

    return {
        is_ctrl_down,
        report_loading: state.gantt_chart_report.report_loading,
        lines_orders_loading: loading,
        report: state.gantt_chart_report,
        created_at: getReportCreatedAt(state.gantt_chart_report),
        properties,
        unscheduled_orders: state.gantt_chart_lines_orders.unscheduled_orders,
        show_extra_lines: planning_table.show_extra_lines,
        shifts,
        shift_x_positions: properties.shift_x_positions,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(UnscheduledGanttChart);
